import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MobileNavigation } from '../components/MobileNavigation';
import { CurrencyDollar, TrendUp, CreditCard, Download, Calendar, Users, CheckCircle, Warning } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminFinancials() {
  const [loading, setLoading] = useState(true);
  const [financialSummary, setFinancialSummary] = useState({});
  const [recentTransactions, setRecentTransactions] = useState([]);
  
  useEffect(() => {
    loadFinancialData();
  }, []);
  
  const loadFinancialData = async () => {
    try {
      // Get financial summary
      const summaryResponse = await fetch(`${getApiBaseUrl()}/api/admin/financial/summary`, {
        credentials: 'include'
      });
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setFinancialSummary(summaryData);
      }
      
      // Get recent transactions
      const transactionsResponse = await fetch(`${getApiBaseUrl()}/api/admin/financial/transactions?limit=20`, {
        credentials: 'include'
      });
      if (transactionsResponse.ok) {
        const transactionsData = await transactionsResponse.json();
        setRecentTransactions(transactionsData);
      }
      
    } catch (error) {
      console.error('Financial data load error:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };
  
  const exportTransactions = () => {
    // Create CSV content
    const csvContent = [
      ['Date', 'Player', 'Session', 'Location', 'Amount', 'Status'].join(','),
      ...recentTransactions.map(t => [
        new Date(t.created_at).toLocaleDateString(),
        t.player?.name || 'Unknown',
        t.session?.date || 'N/A',
        t.session?.location?.name || 'N/A',
        `$${t.amount.toFixed(2)}`,
        t.payment_status
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stay-on-transactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-financials">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-heading">FINANCIAL MANAGEMENT</h1>
              <p className="text-sm text-muted-foreground">
                Revenue tracking and payment management
              </p>
            </div>
            <Button 
              onClick={exportTransactions}
              variant="outline"
              size="sm"
              data-testid="export-transactions-btn"
            >
              <Download size={16} weight="duotone" className="mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Revenue Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <RevenueCard
            title="Today"
            amount={financialSummary.revenue_by_period?.today?.revenue || 0}
            transactions={financialSummary.revenue_by_period?.today?.transactions || 0}
            color="text-primary"
          />
          
          <RevenueCard
            title="This Week"
            amount={financialSummary.revenue_by_period?.week?.revenue || 0}
            transactions={financialSummary.revenue_by_period?.week?.transactions || 0}
            color="text-blue-500"
          />
          
          <RevenueCard
            title="This Month"
            amount={financialSummary.revenue_by_period?.month?.revenue || 0}
            transactions={financialSummary.revenue_by_period?.month?.transactions || 0}
            color="text-green-500"
          />
          
          <RevenueCard
            title="All Time"
            amount={financialSummary.revenue_by_period?.all_time?.revenue || 0}
            transactions={financialSummary.revenue_by_period?.all_time?.transactions || 0}
            color="text-yellow-500"
          />
        </div>
        
        {/* Payment Status Breakdown */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <TrendUp size={20} className="text-primary" weight="duotone" />
              PAYMENT STATUS BREAKDOWN
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {financialSummary.payment_status_breakdown?.map((status) => (
                <div key={status._id} className="text-center p-4 bg-muted/20 rounded-lg">
                  <p className="text-2xl font-bold text-primary">{status.count}</p>
                  <p className="text-sm text-muted-foreground uppercase">{status._id} Payments</p>
                  <p className="text-lg font-semibold">{formatCurrency(status.amount)}</p>
                </div>
              )) || (
                <p className="text-muted-foreground col-span-3 text-center py-4">No payment data available</p>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Top Revenue Sessions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Calendar size={20} className="text-primary" weight="duotone" />
              TOP REVENUE SESSIONS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {financialSummary.top_sessions_by_revenue?.length === 0 || !financialSummary.top_sessions_by_revenue ? (
              <p className="text-muted-foreground text-center py-4">No session revenue data available</p>
            ) : (
              <div className="space-y-3">
                {financialSummary.top_sessions_by_revenue.map((sessionStat, index) => (
                  <div key={sessionStat._id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {sessionStat.session?.date} at {sessionStat.session?.location?.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sessionStat.transactions} transactions
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary text-lg">{formatCurrency(sessionStat.revenue)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Recent Transactions */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <CreditCard size={20} className="text-primary" weight="duotone" />
              RECENT TRANSACTIONS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentTransactions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No transactions found</p>
            ) : (
              <div className="space-y-3">
                {recentTransactions.map((transaction) => (
                  <div key={transaction.payment_id} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm">
                          {transaction.player?.name || 'Unknown Player'}
                        </p>
                        <PaymentStatusBadge status={transaction.payment_status} />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {transaction.session?.date} • {transaction.session?.location?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(transaction.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary text-lg">{formatCurrency(transaction.amount)}</p>
                      <p className="text-xs text-muted-foreground">
                        ID: {transaction.payment_id.slice(-8)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}

function RevenueCard({ title, amount, transactions, color = "text-primary" }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className={`text-2xl font-bold ${color} mb-1`}>{formatCurrency(amount)}</p>
          <p className="text-xs text-muted-foreground">{transactions} transactions</p>
        </div>
      </CardContent>
    </Card>
  );
}

function PaymentStatusBadge({ status }) {
  const styles = {
    paid: 'bg-green-500/20 text-green-400 border-green-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    failed: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  
  const icons = {
    paid: <CheckCircle size={12} />,
    pending: <Warning size={12} />,
    failed: <Warning size={12} />
  };
  
  return (
    <Badge className={`text-xs ${styles[status] || styles.pending} flex items-center gap-1`}>
      {icons[status] || icons.pending}
      {status.toUpperCase()}
    </Badge>
  );
}